import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { AlertTriangle } from 'lucide-react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sortable column item with separate drag handle
const SortableColumnItem = ({ column, onDelete, saving, isDark }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.column_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4 flex items-center gap-3`}
    >
      {/* Drag handle - ONLY this element has drag listeners */}
      <div 
        {...attributes} 
        {...listeners} 
        className="cursor-grab active:cursor-grabbing touch-none"
      >
        <GripVertical className={`w-5 h-5 ${isDark ? 'text-gray-500 hover:text-[#A2FF00]' : 'text-gray-400 hover:text-green-600'}`} />
      </div>
      
      <span className={`flex-1 ${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>{column.name}</span>
      
      {/* Delete button - completely separate from drag */}
      <button
        type="button"
        disabled={saving}
        onClick={(e) => {
          e.stopPropagation();
          if (window.confirm(`Delete column "${column.name}"? This action cannot be undone.`)) {
            onDelete(column.column_id);
          }
        }}
        className="text-gray-400 hover:text-red-500 p-2 transition-colors disabled:opacity-50"
        title="Delete column"
      >
        <Trash2 className="w-5 h-5" />
      </button>
    </div>
  );
};

const ManageMediaWorkflow = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [showWarning, setShowWarning] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newColumnName, setNewColumnName] = useState('');
  const [saving, setSaving] = useState(false);
  const [localColumns, setLocalColumns] = useState([]);
  const [activeColumn, setActiveColumn] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    fetchCurrentUser();
    fetchBoardsData();
  }, []);

  useEffect(() => {
    setLocalColumns(columns);
  }, [columns]);

  useEffect(() => {
    if (selectedBoard) {
      fetchColumnsForBoard(selectedBoard.board_id);
    }
  }, [selectedBoard]);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const user = await response.json();
        setCurrentUser(user);
        if (user.role !== 'admin') {
          navigate('/dashboard');
        }
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      navigate('/');
    }
  };

  const fetchBoardsData = async () => {
    try {
      const boardsResponse = await fetch(`${BACKEND_URL}/api/boards`, { credentials: 'include' });
      if (boardsResponse.ok) {
        const allBoards = await boardsResponse.json();
        const mediaBoards = allBoards.filter(b => b.board_type === 'media_production');
        setBoards(mediaBoards);
        if (mediaBoards.length > 0) {
          setSelectedBoard(mediaBoards[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch boards:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchColumnsForBoard = async (boardId) => {
    try {
      const columnsResponse = await fetch(`${BACKEND_URL}/api/boards/${boardId}/columns`, { credentials: 'include' });
      if (columnsResponse.ok) {
        setColumns(await columnsResponse.json());
      }
    } catch (error) {
      console.error('Failed to fetch columns:', error);
    }
  };

  const handleAddColumn = async () => {
    if (!newColumnName.trim()) {
      alert('Please enter a column name');
      return;
    }
    if (!selectedBoard) {
      alert('No board selected. Please refresh the page.');
      return;
    }
    
    setSaving(true);
    try {
      const maxSortOrder = columns.length > 0 ? Math.max(...columns.map(c => c.sort_order)) : -1;
      
      const response = await fetch(`${BACKEND_URL}/api/boards/${selectedBoard.board_id}/columns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newColumnName,
          sort_order: maxSortOrder + 1
        })
      });

      if (response.ok) {
        setNewColumnName('');
        fetchColumnsForBoard(selectedBoard.board_id);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add column');
      }
    } catch (error) {
      console.error('Add column error:', error);
      alert('Failed to add column: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteColumn = async (columnId) => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/columns/${columnId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchColumnsForBoard(selectedBoard.board_id);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete column');
      }
    } catch (error) {
      console.error('Delete column error:', error);
      alert('Failed to delete column: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const column = localColumns.find(c => c.column_id === active.id);
    setActiveColumn(column);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveColumn(null);

    if (!over || active.id === over.id) return;

    const oldIndex = localColumns.findIndex(c => c.column_id === active.id);
    const newIndex = localColumns.findIndex(c => c.column_id === over.id);

    const reorderedColumns = arrayMove(localColumns, oldIndex, newIndex);
    setLocalColumns(reorderedColumns);

    // Save new order to backend
    setSaving(true);
    try {
      for (let i = 0; i < reorderedColumns.length; i++) {
        const column = reorderedColumns[i];
        await fetch(`${BACKEND_URL}/api/columns/${column.column_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            name: column.name,
            sort_order: i
          })
        });
      }
      fetchColumnsForBoard(selectedBoard.board_id);
    } catch (error) {
      console.error('Reorder error:', error);
      alert('Failed to reorder columns');
      setLocalColumns(columns);
    } finally {
      setSaving(false);
    }
  };

  const handleWarningConfirm = () => {
    setShowWarning(false);
  };

  const handleWarningCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <AppLayout title="Edit Media Workflow">
        <div className="text-[#A2FF00]">Loading...</div>
      </AppLayout>
    );
  }

  if (currentUser?.role !== 'admin') {
    return null;
  }

  return (
    <AppLayout title="Edit Media Workflow">
      {/* Warning Modal */}
      {showWarning && (
        <div className={`fixed inset-0 ${isDark ? 'bg-black/80' : 'bg-black/50'} flex items-center justify-center z-50 p-4`}>
          <div className={`${isDark ? 'bg-[#0A0A0A]' : 'bg-white'} border border-yellow-500/50 rounded-sm max-w-md w-full p-6`}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-10 h-10 text-yellow-500" />
              </div>
              <div className="flex-1">
                <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-3`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  Warning
                </h3>
                <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mb-6`}>
                  Editing this workflow may require a software update test in a sandbox first.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleWarningConfirm}
                    className={`${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black px-6 py-2 font-bold rounded-sm transition-colors`}
                  >
                    I Understand, Continue
                  </button>
                  <button
                    onClick={handleWarningCancel}
                    className={`${isDark ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} px-6 py-2 font-medium rounded-sm transition-colors`}
                  >
                    Go Back
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Column Manager */}
      {!showWarning && (
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Board Selector */}
          {boards.length > 1 && (
            <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`}>
              <h3 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-3`}>Select Board</h3>
              <select
                value={selectedBoard?.board_id || ''}
                onChange={(e) => {
                  const board = boards.find(b => b.board_id === e.target.value);
                  setSelectedBoard(board);
                }}
                className={`w-full ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3`}
              >
                {boards.map(board => (
                  <option key={board.board_id} value={board.board_id}>
                    {board.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Add New Column */}
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`}>
            <h3 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'} mb-3`}>Add New Column</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={newColumnName}
                onChange={(e) => setNewColumnName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddColumn()}
                placeholder="Enter column name..."
                className={`flex-1 ${isDark ? 'bg-[#111] border-white/20 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} border focus:border-[#A2FF00] rounded-sm p-3`}
              />
              <button
                onClick={handleAddColumn}
                disabled={saving || !newColumnName.trim() || !selectedBoard}
                className={`${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-green-500 hover:bg-green-600'} text-black px-6 py-3 font-bold rounded-sm transition-colors disabled:opacity-50 flex items-center gap-2`}
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </div>
            {!selectedBoard && (
              <p className="text-red-400 text-sm mt-2">No media production board found. Please create one first.</p>
            )}
          </div>

          {/* Existing Columns */}
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`}>
            <h3 className="text-sm font-bold text-gray-400 mb-3">
              Existing Columns for {selectedBoard?.name || 'Board'} (Drag to reorder)
            </h3>
            {localColumns.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No columns yet. Add your first column above.</p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={localColumns.map(c => c.column_id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {localColumns.map((column) => (
                      <SortableColumnItem
                        key={column.column_id}
                        column={column}
                        onDelete={handleDeleteColumn}
                        saving={saving}
                        isDark={isDark}
                      />
                    ))}
                  </div>
                </SortableContext>
                
                <DragOverlay>
                  {activeColumn ? (
                    <div className={`${isDark ? 'bg-[#111] border-[#A2FF00]' : 'bg-white border-green-500'} border rounded-sm p-4 flex items-center gap-3 opacity-90 shadow-lg`}>
                      <GripVertical className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
                      <span className={`flex-1 ${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>{activeColumn.name}</span>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </div>

          {/* Info Note */}
          <div className={`${isDark ? 'bg-[#A2FF00]/10 border-[#A2FF00]/20' : 'bg-green-50 border-green-200'} border rounded-sm p-4`}>
            <p className="text-sm text-gray-400">
              <strong className={`${isDark ? 'text-[#A2FF00]' : 'text-green-700'}`}>Note:</strong> Columns with cards cannot be deleted. Move all cards to another column first.
            </p>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default ManageMediaWorkflow;
